import { inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { liveQuery } from 'dexie';
import { from } from 'rxjs';
import { DRIVER, DRIVERS } from '../component/mobility-tab/drivers-sub-tab/drivers-routing.constants';
import ApiV1Adapter from '../database/api/ApiV1Adapter';
import EntityManager from '../database/EntityManager';
import { Driver } from '../database/models';
import { DeviceStateService } from './device-state.service';
import { DialogService } from './dialog.service';
import { KeywordObjectType, KeywordSetService } from './keywordset.service';
import { SnackBarService } from './snack-bar.service';

@Injectable({
  providedIn: 'root',
})
class DriversService extends EntityManager<Driver, 'driver'> {
  private _deviceStateService = inject(DeviceStateService);
  private _dialogService = inject(DialogService);
  private _snackBarService = inject(SnackBarService);
  private _router = inject(Router);
  private _keywordSetService = inject(KeywordSetService);
  public $valuesRecord = signal<Record<number, Driver> | null>(null);

  public values$ = from(
    liveQuery(async () =>
      this.table.toArray(drivers => {
        const storage: Record<number, Driver> = {};
        drivers.forEach(d => {
          storage[d.uid] = d;
        });
        this.$valuesRecord.set(storage);
        return drivers.sort(
          (a, b) => a.firstname.localeCompare(b.firstname) || a.lastname.localeCompare(b.lastname)
        );
      })
    )
  );
  public $values = toSignal(this.values$);

  constructor() {
    super({ name: 'driver', model: Driver, polling: { duration: 6 * 60 * 1000 } }); // 6 minutes (% 20 of location api)
  }

  // TODO : ADD TOASTER

  public getDevices(id: Driver['uid']) {
    const devices = Object.values(this._deviceStateService.$devicesMap() ?? {});
    return devices.filter(device => device.uid_driver === id);
  }

  public async createDriver(driver: Driver, oldKeywords: boolean, newKeywords: string[]) {
    await this.create(driver);
    if (oldKeywords || newKeywords?.length) {
      await this._syncKeywords(driver, newKeywords);
    }
    // this.snackBarService.send({
    //   message: 'SNACK_BAR.CREATED_DRIVER',
    //   type: 'success',
    // });
    if (driver?.uid) this._router.navigate([DRIVER(driver.uid_service, driver.uid)]);
  }

  public async updateDriver(driver: Driver, oldKeywords: boolean, newKeywords: string[]) {
    await this.update(driver.uid, driver);
    if (oldKeywords || newKeywords?.length) {
      await this._syncKeywords(driver, newKeywords);
    }
    // this.snackBarService.send({
    //   message: 'SNACK_BAR.UPDATED_DRIVER',
    //   type: 'success',
    // });
    history.back();
  }

  public deleteDriver(uid: number) {
    this._dialogService.open({
      title: 'DRIVER.DELETEDRIVER',
      content: 'DRIVER.DELETEDRIVER_CONTENT',
      onConfirm: () => {
        this._snackBarService.send({
          message: 'SNACK_BAR.DELETED_DRIVER',
          type: 'success',
        });
        // TODO : delete keywords
        this._router.navigate([DRIVERS]);
        this.delete(uid);
      },
    });
  }

  private async _syncKeywords(driver: Driver, newKeywords: string[]): Promise<void> {
    const { keywords } = await this._keywordSetService.updateKeywords(
      driver.uid,
      KeywordObjectType.Driver,
      driver.keywords,
      newKeywords
    );
    driver.keywords = keywords;
    await this.table.put(driver);
  }

  public async deleteAll(ids: number[]) {
    const api = new ApiV1Adapter('drivers_delete');

    this.table.bulkDelete(ids);

    await api.request({ method: 'GET', body: { uid_drivers: ids } });
  }

  public async updateAll(drivers: Driver[]) {
    // /!\ Be careful, for now, this method its just use to update ui_parent of multiple drivers.
    const api = new ApiV1Adapter('drivers_update');
    const driversIds = drivers.map(({ uid }) => uid);
    this.table.bulkPut(drivers);

    await api.request({
      method: 'GET',
      body: { uid_drivers: driversIds, uid_service: drivers[0].uid_parent },
    });
  }

  public clear(): void {
    this.$valuesRecord.set(null);
  }
}

export default DriversService;
